// Vendor imports
const classNames = require('classnames')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
// Client V2 common imports
const InputText = require('client-v2/common/components/input-text')
const { ModalDepthModifier } = require('client-v2/common/contexts/modal-depth')
const ArticleModelRecord = require('client-v2/common/models/article/record')
const CollectionModelRecord = require('client-v2/common/models/collection/record')
const UserModelRecord = require('client-v2/common/models/user/record')
const tag = require('client-v2/common/utils/hyperscript')
const { stopPropagation } = require('client-v2/common/utils/events')
// ShareModal imports
const ShareModalShareButton = require('client-v2/common/containers/share-modal/components/share-button')
const { socialShareHref } = require('client-v2/common/containers/share-modal/utils')
// Sibling imports
const ShareModalShareFriends = require('./components/share-friends-smart')

const bem = BemEntity('ShareModalV2')

class ShareModalV2 extends React.PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      copied: false,
      hover: false,
      messageText: ''
    }

    // Refs
    this.modalRef = React.createRef()
    this.copyInputRef = React.createRef()

    // Bind event listeners
    this.onCopy = this.onCopy.bind(this)
    this.onShare = this.onShare.bind(this)
    this.onDismissCapture = this.onDismissCapture.bind(this)
    this.onMouseEnter = () => this.setState({ hover: true })
    this.onMouseLeave = () => this.setState({ hover: false })
  }

  componentDidMount() {
    if (typeof window !== 'undefined') {
      document.addEventListener('click', this.onDismissCapture, true)
    }
  }

  componentWillUnmount() {
    if (typeof document !== 'undefined') {
      document.removeEventListener('click', this.onDismissCapture, true)
    }
  }

  componentDidUpdate() {
    // Re-position the modal when its content changes
    if (this.props.onLayoutChange) {
      this.props.onLayoutChange()
    }
  }

  // Event handler methods

  onCopy(event) {
    event.preventDefault()
    this.props.onCopy({
      input: this.copyInputRef.current,
      model: this.props.model
    })
    this.setState(() => ({ copied: true }))
  }

  onShare(event) {
    const { model } = this.props
    const anchor = event.currentTarget
    const href = anchor.getAttribute('href')
    const { shareType } = anchor.dataset
    if (shareType !== 'email') {
      /**
       * `mailto:` links need to be handled by the default `<a href>` click behavior
       * @see https://app.asana.com/0/226524042134185/1137183849467687/f
       */
      event.preventDefault()
    }
    this.props.onShare({ href, model, shareType })
  }

  onDismissCapture(event) {
    const modalEl = this.modalRef.current
    if (!modalEl) {
      return
    }
    let traverseEl = event.target
    while (traverseEl) {
      // Allow clicks on the modal or its children
      if (traverseEl === modalEl) {
        return
      }
      // Walk up the DOM tree to determine if the click is inside the modal
      traverseEl = traverseEl.parentElement
    }
    this.props.onDismiss()
  }

  // Component render methods

  renderSocialButtons() {
    const { isSocialButtonsVisible, model, socialMediums } = this.props
    const { copied } = this.state
    if (!isSocialButtonsVisible) {
      return null
    }
    return tag([
      copied && tag('div', {
        className: bem.element('copied')
      },
        'Copied!'
      ),
      tag('div', {
        className: bem.element('hrefs-container')
      },
        socialMediums.map(medium => (
          tag(ShareModalShareButton, {
            key: medium.id,
            // Values
            className: bem.element('href'),
            href: medium.id === 'copy' ? '' : socialShareHref(model, medium.id),
            name: medium.name,
            shareType: medium.id,
            // Callbacks
            onClick: medium.id === 'copy' ? this.onCopy : this.onShare
          })
        )),
        tag(InputText, {
          inputRef: this.copyInputRef,
          className: bem.element('copy-input'),
          readOnly: true,
          value: socialShareHref(model, 'copy')
        })
      )
    ])
  }

  renderInner() {
    return (
      tag('div', {
        className: bem.element('inner')
      },
        this.renderSocialButtons(),
        tag(ShareModalShareFriends, {
          className: bem.element('friends'),
          ...this.props
        })
      )
    )
  }

  // Root render method

  render() {
    return (
      tag('div', {
        ref: this.modalRef,
        className: classNames(
          this.props.className,
          bem.block()
        ),
        // Swallow click events to prevent going through/behind the modal
        onClick: stopPropagation,
        onMouseEnter: this.onMouseEnter,
        onMouseLeave: this.onMouseLeave
      },
        this.state.hover && tag(ModalDepthModifier),
        this.renderInner()
      )
    )
  }
}

if (__DEV__) {
  // Prop-checking imports
  const PropTypes = require('prop-types')

  ShareModalV2.propTypes = {
    // Flags
    isSocialButtonsVisible: PropTypes.bool,
    // Values
    className: PropTypes.string,
    model: PropTypes.oneOfType([
      PropTypes.shape(ArticleModelRecord.propTypes),
      PropTypes.shape(CollectionModelRecord.propTypes),
      PropTypes.shape(UserModelRecord.propTypes)
    ]),
    socialMediums: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      windowSize: PropTypes.array
    })).isRequired,
    // Callbacks
    onCopy: PropTypes.func.isRequired,
    onDismiss: PropTypes.func.isRequired,
    onLayoutChange: PropTypes.func,
    onShare: PropTypes.func.isRequired
  }
}

module.exports = ShareModalV2
